import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronDown, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import Marquee from '../components/Marquee';
import Counter from '../components/Counter';
import Magnetic from '../components/Magnetic';
import HeroSlider from '../components/HeroSlider';
import Carousel from '../components/Carousel';
import { EASE, ParallaxImg, RevealWord, fadeUp } from '../components/motion';

/* ------------------------------------------------------------------- data */

const services = [
  {
    number: '01',
    kicker: 'MOST REQUESTED',
    title: 'Premium Printing',
    href: '/services/printing',
    image: '/images/hero/large-format-printing.webp',
    imageMobile: '/images/hero/large-format-printing-mobile.webp',
    caption: 'Shot in our Mthatha studio',
    description:
      'Banners, signage, vehicle wraps and large-format work produced on professional-grade equipment — with fast turnaround and nationwide delivery.',
    tags: ['Banners', 'Vehicle wraps', 'Signage', 'Apparel'],
    cta: 'Explore printing',
  },
  {
    number: '02',
    kicker: 'DESIGN',
    title: 'Branding & Design',
    href: '/services/branding',
    image: '/images/hero/office-branding.webp',
    imageMobile: '/images/hero/office-branding-mobile.webp',
    description:
      'Strategic identities built to connect — logos, packaging, corporate stationery and full brand systems that make small businesses look established.',
    tags: ['Logo & identity', 'Packaging', 'Stationery'],
    cta: 'See branding work',
  },
  {
    number: '03',
    kicker: 'NEW',
    title: 'Digital Services',
    href: '/services/digital-services',
    image: '/images/hero/web-services.webp',
    imageMobile: '/images/hero/web-services-mobile.webp',
    description:
      'Websites, mobile apps and the automations that connect them — designed, coded and launched by the same team that builds your brand. Fixed-price quotes, mobile-first, SEO-ready.',
    tags: ['Web design', 'App development', 'Automation'],
    cta: 'Explore digital services',
  },
  {
    number: '04',
    kicker: 'FOUNDATIONS',
    title: 'Business Solutions',
    href: '/services/company-registration',
    image: '/images/hero/print-marketing.webp',
    imageMobile: '/images/hero/print-marketing-mobile.webp',
    description:
      'Company registration, compliance and strategic planning — the paperwork handled properly, so you can focus on the work itself.',
    tags: ['CIPC registration', 'Compliance', 'Strategy'],
    cta: 'Get registered',
  },
];

const stats = [
  { value: '1000+', label: 'Satisfied clients' },
  { value: '500+', label: 'Businesses registered' },
  { value: '1000+', label: 'Projects delivered' },
];

const work = [
  {
    src: '/images/our-work/our-work-mock-up-bottle.jpg',
    title: 'Bottle Branding',
    meta: 'Packaging · Label design · Print production',
    tag: 'Branding',
    span: 'lg:col-span-12',
    aspect: 'aspect-[16/10] lg:aspect-[21/10]',
    hero: true,
  },
  {
    src: '/images/our-work/our-work-tshirt-mockup.jpg',
    title: 'Apparel Print',
    meta: 'T-shirts · Workwear',
    tag: 'Printing',
    span: 'lg:col-span-6',
    aspect: 'aspect-[16/10]',
  },
  {
    src: '/images/our-work/our-work-business-card.jpg',
    title: 'Corporate Stationery',
    meta: 'Business cards · Letterheads',
    tag: 'Identity',
    span: 'lg:col-span-6',
    aspect: 'aspect-[16/10]',
  },
  {
    src: '/images/our-work/our-work-packaging.jpg',
    title: 'Product Packaging',
    meta: 'Boxes · Sleeves · Labels',
    tag: 'Packaging',
    span: 'lg:col-span-12',
    aspect: 'aspect-[16/10] lg:aspect-[21/10]',
    hero: true,
  },
];

const quotes = [
  {
    text: '"Quality of service was 100%. The designs were a perfect fit to my cause."',
    who: 'Msa Isivile Mangqasha',
    role: 'Satisfied Client',
  },
  {
    text: '"For graphic design and digital creations, they top the list."',
    who: 'Lele Mbinda',
    role: 'Design Client',
  },
  {
    text: '"The vehicle branding for our nationwide fleet significantly increased our visibility."',
    who: 'Themba Nkosi',
    role: 'Director, National Fleet Services',
  },
];

/* ------------------------------------------------------------------- page */

const Home = () => {
  /* Rotating testimonial */
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % quotes.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <SEO
        title="Print Shop, Branding & Company Registration in Mthatha"
        description="DKSN at the Mall of Mthatha: large-format printing, banners & signage, logo design, and CIPC company registration. Visit Shop 23B or get a free quote — fast turnaround, nationwide delivery."
        path="/"
        image="/images/dksn-store-front-og.jpeg"
        imageAlt="DKSN Business Solutions storefront at the Mall of Mthatha"
      />
      <div className="min-h-screen bg-primary-950 text-white">
        {/* ============================================== HERO — full bleed with slider */}
        <section className="relative flex min-h-[100svh] items-end overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <HeroSlider autoPlayInterval={5000} />
          </div>

          <div className="section-container relative z-10 w-full pb-24 pt-40">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9, ease: EASE }}
              className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-azure-400 shadow-[0_0_12px_3px_rgba(110,155,242,0.9)]" />
              <span className="text-sm font-medium text-gray-200">
                Mthatha · Eastern Cape · Nationwide delivery
              </span>
            </motion.div>

            <h1 className="mb-9 max-w-6xl font-display text-6xl font-bold leading-[0.96] tracking-[-0.045em] sm:text-7xl lg:text-8xl xl:text-[8rem]">
              <RevealWord delay={0.15}>Print.&nbsp;</RevealWord>
              <RevealWord delay={0.28}>Brand.&nbsp;</RevealWord>
              <RevealWord delay={0.41} className="font-serif font-normal italic tracking-[-0.02em] text-azure-300">
                Grow.
              </RevealWord>
            </h1>

            <div className="flex flex-wrap items-end justify-between gap-10">
              <motion.p
                initial={{ y: 12 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="max-w-md text-lg leading-relaxed text-gray-300"
              >
                From company registration to vehicle wraps — DKSN is the studio behind Eastern
                Cape's boldest businesses.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.75, ease: EASE }}
                className="flex flex-wrap items-center gap-3.5"
              >
                <Magnetic>
                  <Link
                    to="/contact"
                    className="group inline-flex items-center gap-2.5 rounded-full bg-gray-100 px-8 py-4 text-base font-semibold text-primary-950 shadow-[0_18px_50px_-16px_rgba(255,255,255,0.5)] transition-colors hover:bg-white"
                  >
                    Start Your Project
                    <ArrowUpRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </Magnetic>
                <Link
                  to="/our-work"
                  className="inline-flex items-center rounded-full px-7 py-4 text-base font-medium text-white backdrop-blur-md transition-transform duration-300 hover:-translate-y-px"
                  style={{
                    border: '1px solid transparent',
                    background:
                      'linear-gradient(rgba(255,255,255,0.07),rgba(255,255,255,0.07)) padding-box, linear-gradient(120deg, rgba(255,255,255,0.65), rgba(255,255,255,0.1) 30%, rgba(157,190,247,0.12) 62%, rgba(157,190,247,0.65)) border-box',
                  }}
                >
                  See Our Work
                </Link>
              </motion.div>
            </div>
          </div>

          <motion.div
            aria-hidden="true"
            className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-gray-400"
            animate={{ y: [0, 8, 0], opacity: [1, 0.4, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown className="h-6 w-6" />
          </motion.div>
        </section>

        {/* ===================================================== MARQUEE */}
        <Marquee
          items={[
            'Large-Format Printing',
            'Vehicle Wraps',
            'Company Registration',
            'Brand Identity',
            'Signage',
            'Corporate Gifts',
          ]}
        />

        {/* ===================== SERVICES — sticky-stacking editorial splits */}
        <section className="mx-auto max-w-[1440px] px-4 pt-16 sm:px-10 sm:pt-36">
          <motion.div
            {...fadeUp}
            className="mb-16 flex flex-wrap items-end justify-between gap-8 sm:mb-20"
          >
            <h2 className="max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              Everything your business needs to look and feel{' '}
              <span className="font-serif font-normal italic text-azure-300">Premium!</span>
            </h2>
            <p className="max-w-sm leading-relaxed text-gray-500">
              Four disciplines, one studio. We take brands from paperwork to full-scale
              production — and now websites — under one roof.
            </p>
          </motion.div>

          {services.map((s, i) => {
            const imageFirst = i % 2 === 0;
            const imageBlock = (
              <div key="img">
                <ParallaxImg
                  src={s.image}
                  srcMobile={s.imageMobile}
                  alt={s.title}
                  speed={14}
                  className="aspect-[16/11] rounded-3xl"
                />
              </div>
            );
            const textBlock = (
              <div key="txt">
                <div className="mb-4 font-display text-sm font-semibold tracking-[0.12em] text-azure-500">
                  {s.number} — {s.kicker}
                </div>
                <h3 className="mb-4 font-display text-3xl font-bold tracking-[-0.03em] sm:text-4xl">
                  {s.title}
                </h3>
                <p className="mb-7 max-w-xl leading-[1.7] text-gray-400">{s.description}</p>
                <div className="mb-8 flex flex-wrap gap-2">
                  {s.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[13px] font-medium text-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={s.href}
                  className="group inline-flex items-center gap-2 font-semibold text-azure-300 transition-colors hover:text-azure-200"
                >
                  {s.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            );
            return (
              <div
                key={s.number}
                style={{ zIndex: i + 1 }}
                className={`sticky top-[88px] grid min-h-[min(80vh,780px)] content-center items-center gap-10 border-t border-white/[0.08] bg-primary-950 py-10 lg:gap-16 ${
                  imageFirst ? 'lg:grid-cols-[1.15fr_0.85fr]' : 'lg:grid-cols-[0.85fr_1.15fr]'
                }`}
              >
                {imageFirst ? [imageBlock, textBlock] : [textBlock, imageBlock]}
              </div>
            );
          })}
        </section>

        {/* ================================== STATS — full-bleed photo band */}
        <section className="relative mt-16 overflow-hidden sm:mt-36">
          <ParallaxImg
            src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&auto=format&fit=crop&q=75"
            alt="Team at work"
            speed={20}
            className="absolute inset-0"
            overlayClassName="bg-gradient-to-b from-primary-950 via-primary-950/80 to-primary-950"
          />
          <div className="section-container relative grid gap-10 py-16 text-center sm:grid-cols-3 sm:py-40">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1, delay: i * 0.12, ease: EASE }}
              >
                <p className="bg-gradient-to-b from-white to-azure-300 bg-clip-text font-display text-6xl font-bold leading-none tracking-[-0.05em] text-transparent sm:text-7xl lg:text-8xl">
                  <Counter value={stat.value} />
                </p>
                <p className="mt-3 text-[15px] font-medium tracking-wide text-gray-300">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ======================================= WORK — asymmetric grid */}
        <section className="section-container pt-16 sm:pt-36">
          <motion.div
            {...fadeUp}
            className="mb-14 flex flex-wrap items-end justify-between gap-8"
          >
            <h2 className="font-display text-4xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              Made in Mthatha,
              <span className="block font-serif font-normal italic text-azure-300">
                shipped nationwide.
              </span>
            </h2>
            <Link
              to="/our-work"
              className="group inline-flex items-center gap-2 whitespace-nowrap font-semibold text-azure-300 transition-colors hover:text-azure-200"
            >
              View full portfolio
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <Carousel slideClassName="w-[82%]" desktopGridClassName="sm:grid sm:grid-cols-2 lg:grid-cols-12 sm:gap-4">
            {work.map((item) => (
              <div
                key={item.src}
                className={`group relative ${item.aspect} overflow-hidden rounded-3xl sm:h-full ${item.span}`}
              >
                <Link to="/our-work" aria-label={`View ${item.title}`}>
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  />
                  <div
                    className={`absolute inset-0 flex items-end justify-between gap-5 bg-gradient-to-b from-transparent via-transparent to-primary-950/85 ${
                      item.hero ? 'p-9' : 'p-7'
                    }`}
                  >
                    <div>
                      <p
                        className={`font-display font-bold tracking-tight ${
                          item.hero ? 'text-2xl sm:text-3xl' : 'text-xl'
                        }`}
                      >
                        {item.title}
                      </p>
                      <p className="mt-1.5 text-sm text-gray-400">{item.meta}</p>
                    </div>
                    <span className="hidden shrink-0 whitespace-nowrap rounded-full border border-white/20 bg-primary-950/50 px-4 py-2 text-[13px] font-medium text-gray-200 backdrop-blur-md sm:inline-block">
                      {item.tag}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </Carousel>
        </section>

        {/* ============================= TESTIMONIAL — single rotating quote */}
        <section className="mx-auto max-w-4xl px-4 pt-16 text-center sm:px-6 sm:pt-40">
          <motion.div {...fadeUp}>
            <div className="mb-8 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-[18px] w-[18px] fill-azure-500 text-azure-500" />
              ))}
            </div>
            <div className="min-h-[180px] sm:min-h-[160px]">
              <AnimatePresence mode="wait">
                <motion.blockquote
                  key={quoteIdx}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="font-serif text-3xl italic leading-[1.3] tracking-[-0.01em] text-white sm:text-4xl lg:text-[2.75rem]">
                    {quotes[quoteIdx].text}
                  </p>
                  <footer className="mt-9">
                    <p className="text-[15px] font-semibold">{quotes[quoteIdx].who}</p>
                    <p className="mt-1 text-sm text-gray-500">{quotes[quoteIdx].role}</p>
                  </footer>
                </motion.blockquote>
              </AnimatePresence>
            </div>
            <div className="mt-9 flex justify-center gap-2" role="tablist" aria-label="Testimonials">
              {quotes.map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === quoteIdx}
                  aria-label={`Testimonial ${i + 1}`}
                  onClick={() => setQuoteIdx(i)}
                  className={`h-1 w-7 rounded-full transition-colors duration-300 ${
                    i === quoteIdx ? 'bg-azure-500' : 'bg-white/15 hover:bg-white/30'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </section>

        {/* ===================================== CTA — full-bleed statement */}
        <section className="relative mt-16 overflow-hidden sm:mt-40">
          <ParallaxImg
            src="https://images.unsplash.com/photo-1573164574511-73c773193279?w=2000&auto=format&fit=crop&q=75"
            alt="Planning session"
            speed={18}
            className="absolute inset-0"
            overlayClassName="bg-gradient-to-b from-primary-950 via-primary-950/85 to-primary-950/90"
          />
          <motion.div
            {...fadeUp}
            className="relative mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-44"
          >
            <h2 className="mb-6 font-display text-5xl font-bold leading-[1.02] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
              Let's build something{' '}
              <span className="font-serif font-normal italic text-azure-300">
                worth talking about.
              </span>
            </h2>
            <p className="mx-auto mb-10 max-w-xl text-lg leading-relaxed text-gray-300">
              Get a quote in minutes. Join hundreds of businesses across South Africa who trust
              DKSN.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3.5">
              <Magnetic>
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-gray-100 px-9 py-[19px] text-base font-semibold text-primary-950 shadow-[0_20px_60px_-18px_rgba(255,255,255,0.55)] transition-colors hover:bg-white"
                >
                  Start Your Project
                  <ArrowUpRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Magnetic>
              <a
                href="https://wa.me/27679210514"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full px-8 py-[19px] text-base font-medium text-white backdrop-blur-md transition-transform duration-300 hover:-translate-y-px"
                style={{
                  border: '1px solid transparent',
                  background:
                    'linear-gradient(rgba(255,255,255,0.07),rgba(255,255,255,0.07)) padding-box, linear-gradient(120deg, rgba(255,255,255,0.65), rgba(255,255,255,0.1) 30%, rgba(157,190,247,0.12) 62%, rgba(157,190,247,0.65)) border-box',
                }}
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Home;
